import React from "react";
import FileUpload from "./FileUpload";
import { getVisibleDocuments } from "utilities/schemaEval";

/**
 * Schema-driven documents step renderer.
 * Renders file upload fields from the schema's documents array.
 * Supports conditional visibility and per-owner document expansion.
 */
const DocumentsStepRenderer = ({
  documentSchema,
  data,
  handleDocumentUpload,
  handleDocumentDelete,
}) => {
  const documents = data?.documents || [];

  // Get visible documents with per-owner expansion
  const visibleDocs = getVisibleDocuments(documentSchema, data);

  const getFilesByType = (type) => {
    const docEntry = documents.find((doc) => doc.type === type);
    return docEntry?.files || [];
  };

  const handleUpload = async (name, files) => {
    await handleDocumentUpload(files, name);
  };

  const handleDelete = async (name, key) => {
    await handleDocumentDelete(key, name);
  };

  const COMMON_PROPS = {
    onUpload: handleUpload,
    onDelete: handleDelete,
    accept: ".pdf,.jpg,.jpeg,.png",
  };

  const requiredDocs = visibleDocs.filter((d) => d.required);
  const optionalDocs = visibleDocs.filter((d) => !d.required);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-normal text-foreground mb-2">
          Required Documents<span className="text-brand">.</span>
        </h2>
        <p className="text-muted-foreground font-light">
          Upload the necessary documents to complete your registration. These
          help us verify your business and expedite processor applications.
        </p>
      </div>

      {/* Required documents */}
      {requiredDocs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {requiredDocs.map((docType) => (
            <FileUpload
              key={docType.type}
              label={docType.label}
              name={docType.type}
              files={getFilesByType(docType.type)}
              required={docType.required}
              description={docType.description}
              {...COMMON_PROPS}
            />
          ))}
        </div>
      )}

      {/* Optional documents */}
      {optionalDocs.length > 0 && (
        <>
          <h3 className="text-lg font-normal text-muted-foreground mt-8">
            Optional Documents
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {optionalDocs.map((docType) => (
              <FileUpload
                key={docType.type}
                label={docType.label}
                name={docType.type}
                files={getFilesByType(docType.type)}
                required={docType.required}
                description={docType.description}
                {...COMMON_PROPS}
              />
            ))}
          </div>
        </>
      )}

      <div className="bg-muted/30 border border-brand/30 rounded-lg p-6">
        <h3 className="text-lg font-normal text-brand mb-2">
          Document Guidelines
        </h3>
        <ul className="text-sm text-muted-foreground font-light space-y-1">
          <li>All documents should be clear and legible</li>
          <li>Accepted formats: PDF, JPG, JPEG, PNG</li>
          <li>Maximum file size: 10MB per document</li>
          <li>Bank statements must show business name and account details</li>
          <li>
            Processing statements help us understand your current situation
          </li>
          <li>
            Additional documents may be requested based on your business type
          </li>
        </ul>
      </div>
    </div>
  );
};

export default DocumentsStepRenderer;
